import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, exhaustMap } from 'rxjs';

import { APP_STORE } from '@core/stores/app.store';
import { ATTACHMENT_DOWNLOAD_ERRORS } from '@shared/models/attachment-download-errors';
import { AttachmentsZipService } from '@file-upload/services/attachments-zip.service';
import { Notification as AppNotification } from '@notifications/models/notification';
import { NotificationStatus } from '@notifications/models/notification-status';
import { NotificationType } from '@notifications/models/notification-type';
import { ToastNotificationsService } from '@notifications/services/toast-notifications.service';
import { NOTIFICATIONS_STORE } from '@notifications/stores/notifications.store';

@Injectable()
export class AttachmentsZipDownloadService {
  readonly #attachmentsZipService = inject(AttachmentsZipService);
  readonly #toastNotificationsService = inject(ToastNotificationsService);
  readonly #appStore = inject(APP_STORE);
  readonly #notificationsStore = inject(NOTIFICATIONS_STORE);

  readonly downloadInspectionAttachments = rxMethod<{
    inspectionId: number;
    inspectionName: string;
  }>(
    pipe(
      exhaustMap(({ inspectionId, inspectionName }) => {
        const clientId = this.#appStore.selectedClientId();
        const projectId = this.#appStore.selectedProjectId();

        return this.#attachmentsZipService
          .downloadInspectionAttachments(inspectionId)
          .pipe(
            tapResponse({
              next: (notification: AppNotification) => {
                this.#notificationsStore.addOrUpdateNotification({
                  ...notification,
                  date: new Date(),
                  type: NotificationType.InspectionAttachmentsDownload,
                  status: NotificationStatus.InProgress,
                  templateVariables: {
                    inspectionName
                  },
                  initialDelay: 5,
                  delay: 3,
                  clientId,
                  projectId
                });
              },
              error: (error: HttpErrorResponse) => {
                console.error(
                  'Error downloading inspection attachments zip',
                  error
                );
                this.handleAttachmentsDownloadError(
                  error,
                  'Inspection attachments download failed'
                );
              }
            })
          );
      })
    )
  );

  readonly downloadInspectionItemAttachments = rxMethod<{
    inspectionId: number;
    inspectionItemId: number;
    inspectionItemName: string;
  }>(
    pipe(
      exhaustMap(({ inspectionId, inspectionItemId, inspectionItemName }) => {
        const clientId = this.#appStore.selectedClientId();
        const projectId = this.#appStore.selectedProjectId();

        return this.#attachmentsZipService
          .downloadInspectionItemAttachments(inspectionId, inspectionItemId)
          .pipe(
            tapResponse({
              next: (notification: AppNotification) => {
                this.#notificationsStore.addOrUpdateNotification({
                  ...notification,
                  date: new Date(),
                  type: NotificationType.InspectionItemAttachmentsDownload,
                  status: NotificationStatus.InProgress,
                  templateVariables: {
                    inspectionItemName
                  },
                  initialDelay: 5,
                  delay: 3,
                  clientId,
                  projectId
                });
              },
              error: (error: HttpErrorResponse) => {
                this.handleAttachmentsDownloadError(
                  error,
                  'Inspection item attachments download failed'
                );
              }
            })
          );
      })
    )
  );

  readonly downloadLopAttachments = rxMethod<{
    lopItemId: number;
    lopItemName: string;
  }>(
    pipe(
      exhaustMap(({ lopItemId, lopItemName }) => {
        const clientId = this.#appStore.selectedClientId();
        const projectId = this.#appStore.selectedProjectId();

        return this.#attachmentsZipService
          .downloadLopItemAttachments(lopItemId)
          .pipe(
            tapResponse({
              next: (notification: AppNotification) => {
                this.#notificationsStore.addOrUpdateNotification({
                  ...notification,
                  date: new Date(),
                  type: NotificationType.LopItemAttachmentsDownload,
                  status: NotificationStatus.InProgress,
                  templateVariables: {
                    lopItemName
                  },
                  initialDelay: 5,
                  delay: 3,
                  clientId,
                  projectId
                });
              },
              error: (error: HttpErrorResponse) => {
                this.handleAttachmentsDownloadError(
                  error,
                  'LOP item attachments download failed'
                );
              }
            })
          );
      })
    )
  );

  private handleAttachmentsDownloadError(
    error: HttpErrorResponse,
    fallbackMessage: string
  ): void {
    const payload = error.error;

    if (payload && typeof payload === 'object') {
      const key = Object.keys(payload).find(
        k => k in ATTACHMENT_DOWNLOAD_ERRORS
      );

      if (key) {
        const entry = ATTACHMENT_DOWNLOAD_ERRORS[key];

        this.#toastNotificationsService[entry.type](entry.message);

        return;
      }
    }

    this.#toastNotificationsService.error(fallbackMessage);
  }
}
